/**
 * Encrypted attachments kept in this browser
 * (docs/plans/2026-09-29-attachments-move-with-their-map.md), one database
 * per map and scope, each file in the server's download format:
 *
 *   device:  `orbital-attachments-<docId>`: a map stored on this device;
 *            kept until the user deletes the file or the map.
 *   offline: `orbital-offline-attachments-<docId>`: copies of an online map's
 *            files for offline use; account data, removed at sign-out and the
 *            first thing dropped when space is short.
 *
 *   waiting: `orbital-waiting-attachments-<docId>`: files attached to an
 *            online map that could not reach the server yet; they exist
 *            nowhere else: removed once sent, with the map, or at sign-out
 *            after a warning.
 *
 * Deleting the account removes all three (every `orbital-*` database).
 */

import { readEnvelopeHeader } from "../services/attachment-envelope.js";

// Two stores so that listing a map's files reads no file bytes: `files`
// holds the bytes, `meta` the header, size and dates.
const FILES = "files";
const META = "meta";

export function attachmentDbName(scope, docId) {
  if (scope === "device") return `orbital-attachments-${docId}`;
  if (scope === "offline") return `orbital-offline-attachments-${docId}`;
  if (scope === "waiting") return `orbital-waiting-attachments-${docId}`;
  throw new Error(`Unknown attachment scope: ${scope}`);
}

function request(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function open(scope, docId) {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(attachmentDbName(scope, docId), 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(FILES);
      req.result.createObjectStore(META);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("Attachment storage is busy in another tab"));
  });
}

async function withStores(scope, docId, mode, names, fn) {
  const db = await open(scope, docId);
  try {
    const tx = db.transaction(names, mode);
    const done = new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error("Attachment write aborted"));
    });
    const result = await fn(...names.map((name) => tx.objectStore(name)));
    await done;
    return result;
  } finally {
    db.close();
  }
}

/** Store one attachment's bytes (a Uint8Array in the download format). */
export function putStoredAttachment(scope, docId, blobId, bytes) {
  const copy = bytes.slice().buffer; // detached from the caller's view
  const header = readEnvelopeHeader(copy);
  const now = Date.now();
  return withStores(scope, docId, "readwrite", [FILES, META], async (files, meta) => {
    files.put(copy, blobId);
    await request(meta.put({ header, size: copy.byteLength, storedAt: now, usedAt: now }, blobId));
  });
}

/** One attachment's bytes, or null. Marks it used (the oldest copies are dropped first). */
export async function getStoredAttachment(scope, docId, blobId) {
  return withStores(scope, docId, "readwrite", [FILES, META], async (files, meta) => {
    const bytes = await request(files.get(blobId));
    if (!bytes) return null;
    const info = await request(meta.get(blobId));
    // Rewrite the date at most once a day, not on every display.
    if (info && Date.now() - (info.usedAt || 0) > 86400000) meta.put({ ...info, usedAt: Date.now() }, blobId);
    return new Uint8Array(bytes);
  });
}

export function deleteStoredAttachment(scope, docId, blobId) {
  return withStores(scope, docId, "readwrite", [FILES, META], async (files, meta) => {
    files.delete(blobId);
    await request(meta.delete(blobId));
  });
}

/** @returns {Promise<Array<{id: string, size: number, usedAt: number, header: object|null}>>} */
export function listStoredAttachments(scope, docId) {
  return withStores(scope, docId, "readonly", [META], (meta) => new Promise((resolve, reject) => {
    const out = [];
    const cursor = meta.openCursor();
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (!c) return resolve(out);
      out.push({ id: String(c.key), size: c.value.size, usedAt: c.value.usedAt || 0, header: c.value.header || null });
      c.continue();
    };
    cursor.onerror = () => reject(cursor.error);
  }));
}

/**
 * The maps with at least one file in this scope; with `withEmpty`, also the
 * maps whose files were all removed (their database is still there).
 */
export async function listStoredAttachmentMaps(scope, { withEmpty = false } = {}) {
  if (!indexedDB.databases) return [];
  const prefix = attachmentDbName(scope, "");
  const docIds = (await indexedDB.databases()).map((db) => db.name || "")
    .filter((name) => name.startsWith(prefix)).map((name) => name.slice(prefix.length));
  if (withEmpty) return docIds;
  const out = [];
  for (const docId of docIds) {
    try {
      if ((await listStoredAttachments(scope, docId)).length) out.push(docId);
    } catch { /* unreadable: skipped */ }
  }
  return out;
}

/** Remove all attachments of one map in one scope; returns how many there were. */
export async function deleteStoredMapAttachments(scope, docId) {
  let count = 0;
  try { count = (await listStoredAttachments(scope, docId)).length; } catch { /* unreadable: delete anyway */ }
  await new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(attachmentDbName(scope, docId));
    req.onsuccess = resolve;
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("Attachment storage is still open in another tab"));
  });
  return count;
}
